<template>
  <div class="field-table-root">
    <!-- =========================
         CONFIGURE MODE
         ========================= -->
    <div v-if="mode === 'configure'" class="table-configurator">
      <div class="tc-header">
        <h3 class="tc-title">Configure Table Field</h3>
        <p class="tc-subtitle">
          Define the table structure and configure each column using the same field constraints dialog
          used elsewhere in the builder.
        </p>
      </div>

      <div class="tc-grid">
        <div class="tc-group">
          <label>Field label</label>
          <input
            v-model.trim="localConfig.label"
            type="text"
            placeholder="e.g. Lab Results Table"
          />
        </div>

        <div class="tc-group">
          <label>Initial rows</label>
          <input
            v-model.number="localConfig.initialRows"
            type="number"
            min="1"
            max="500"
          />
        </div>
      </div>

      <div class="tc-toggles">
        <label class="tc-checkbox">
          <input type="checkbox" v-model="localConfig.allowAddRows" />
          Allow adding rows during data entry
        </label>

        <label class="tc-checkbox">
          <input type="checkbox" v-model="localConfig.showRowNumbers" />
          Show row numbers
        </label>
      </div>

      <div class="tc-section">
        <div class="tc-section-header">
          <h4>Table Settings</h4>
          <button class="btn-option" type="button" @click="openTableSettings">
            Table settings
          </button>
        </div>

        <div class="tc-column-summary">
          <span class="tc-chip">Table</span>

          <span v-if="tableSummary()" class="tc-summary-text">
            {{ tableSummary() }}
          </span>
          <span v-else class="tc-summary-text muted">
            No extra table constraints configured
          </span>
        </div>
      </div>

      <div class="tc-section">
        <div class="tc-section-header">
          <h4>Columns</h4>
          <button class="btn-option" type="button" @click="addColumn">
            + Add Column
          </button>
        </div>

        <div
          v-for="(col, idx) in localConfig.columns"
          :key="col.id"
          class="tc-column-card"
        >
          <div class="tc-column-top">
            <strong>Column {{ idx + 1 }}</strong>

            <div class="tc-column-actions">
              <button
                type="button"
                class="icon-btn"
                :disabled="idx === 0"
                title="Move up"
                @click="moveColumn(idx, -1)"
              >
                ↑
              </button>

              <button
                type="button"
                class="icon-btn"
                :disabled="idx === localConfig.columns.length - 1"
                title="Move down"
                @click="moveColumn(idx, 1)"
              >
                ↓
              </button>

              <button
                type="button"
                class="icon-btn"
                title="Column settings"
                @click="openColumnSettings(idx)"
              >
                <i :class="icons.cog"></i>
              </button>

              <button
                type="button"
                class="icon-btn danger"
                :disabled="localConfig.columns.length === 1"
                title="Delete column"
                @click="removeColumn(idx)"
              >
                Delete
              </button>
            </div>
          </div>

          <div class="tc-column-grid">
            <div class="tc-group">
              <label>Column label</label>
              <input
                v-model.trim="col.label"
                type="text"
                :placeholder="`Column ${idx + 1}`"
              />
            </div>

            <div class="tc-group">
              <label>Column type</label>
              <select v-model="col.type" @change="onTypeChanged(col)">
                <option v-for="t in columnTypeOptions" :key="t.value" :value="t.value">
                  {{ t.label }}
                </option>
              </select>
            </div>
          </div>

          <div class="tc-column-summary">
            <span class="tc-chip">{{ prettyType(col.type) }}</span>

            <span v-if="columnSummary(col)" class="tc-summary-text">
              {{ columnSummary(col) }}
            </span>
            <span v-else class="tc-summary-text muted">
              No extra constraints configured
            </span>
          </div>
        </div>
      </div>

      <div class="tc-section">
        <h4>Preview</h4>

        <div class="table-runtime-wrap preview-wrap">
          <table class="table-runtime">
            <thead>
              <tr>
                <th v-if="localConfig.showRowNumbers" class="rowno-col">#</th>
                <th
                  v-for="item in previewColumnRenderList"
                  :key="item.col.id || item.colIdx"
                >
                  <div class="table-head-title">
                    <span>{{ item.col.label || 'Untitled column' }}</span>
                    <span v-if="item.col.constraints?.required" class="required-star">*</span>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(row, rowIndex) in previewRows" :key="`preview-row-${rowIndex}`">
                <td v-if="localConfig.showRowNumbers" class="rowno-col">{{ rowIndex + 1 }}</td>

                <td
                  v-for="item in previewColumnRenderList"
                  :key="`${rowIndex}-${item.col.id || item.colIdx}`"
                >
                  <div
                    v-if="isPreviewColumnVisible(item.colIdx, rowIndex)"
                    class="table-cell-box"
                  >
                    <div class="table-cell-control">
                      <input
                        v-if="item.col.type === 'text'"
                        type="text"
                        :value="row[getPreviewColKey(item.col, item.colIdx)]"
                        :placeholder="item.col.constraints?.placeholder || ''"
                        readonly
                      />

                      <textarea
                        v-else-if="item.col.type === 'textarea'"
                        :value="row[getPreviewColKey(item.col, item.colIdx)]"
                        :placeholder="item.col.constraints?.placeholder || ''"
                        rows="3"
                        readonly
                      ></textarea>

                      <input
                        v-else-if="item.col.type === 'number'"
                        type="number"
                        :value="row[getPreviewColKey(item.col, item.colIdx)]"
                        :min="item.col.constraints?.min"
                        :max="item.col.constraints?.max"
                        :step="item.col.constraints?.step"
                        readonly
                      />

                      <DateFormatPicker
                        v-else-if="item.col.type === 'date'"
                        :modelValue="row[getPreviewColKey(item.col, item.colIdx)]"
                        :format="item.col.constraints?.dateFormat || 'dd.MM.yyyy'"
                        :placeholder="item.col.constraints?.placeholder || (item.col.constraints?.dateFormat || 'dd.MM.yyyy')"
                        :min-date="item.col.constraints?.minDate || null"
                        :max-date="item.col.constraints?.maxDate || null"
                        :readonly="true"
                      />

                      <FieldTime
                        v-else-if="item.col.type === 'time'"
                        :modelValue="row[getPreviewColKey(item.col, item.colIdx)]"
                        :placeholder="item.col.constraints?.placeholder || (item.col.constraints?.timeFormat || 'HH:mm')"
                        v-bind="item.col.constraints || {}"
                        :readonly="true"
                      />

                      <FieldSelect
                        v-else-if="item.col.type === 'select'"
                        :modelValue="row[getPreviewColKey(item.col, item.colIdx)]"
                        :options="item.col.options || []"
                        :multiple="!!item.col.constraints?.allowMultiple"
                        :allow-other="!!item.col.constraints?.allowOther"
                        :readonly="true"
                        :default-value="item.col.constraints?.defaultValue"
                        :placeholder="'Select…'"
                      />

                      <FieldRadioGroup
                        v-else-if="item.col.type === 'radio'"
                        :name="`preview_radio_${rowIndex}_${item.col.id || item.colIdx}`"
                        :options="item.col.options || []"
                        :modelValue="row[getPreviewColKey(item.col, item.colIdx)]"
                        :default-value="item.col.constraints?.defaultValue"
                        v-bind="item.col.constraints || {}"
                        :disabled="true"
                      />

                      <FieldCheckbox
                        v-else-if="item.col.type === 'checkbox'"
                        :modelValue="row[getPreviewColKey(item.col, item.colIdx)]"
                        v-bind="item.col.constraints || {}"
                        :disabled="true"
                      />

                      <input
                        v-else
                        type="text"
                        :value="row[getPreviewColKey(item.col, item.colIdx)]"
                        :placeholder="item.col.constraints?.placeholder || ''"
                        readonly
                      />
                    </div>

                    <div v-if="item.col.constraints?.helpText" class="cell-help">
                      {{ item.col.constraints.helpText }}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="tc-preview-footer">
          <span>Initial rows: {{ localConfig.initialRows }}</span>
          <span>Columns: {{ localConfig.columns.length }}</span>
          <span>{{ localConfig.allowAddRows ? "Rows can be expanded later" : "Rows fixed initially" }}</span>
        </div>
      </div>

      <div class="modal-actions">
        <button class="btn-primary" type="button" @click="saveConfig">Save Table</button>
        <button class="btn-option" type="button" @click="$emit('cancel')">Cancel</button>
      </div>

      <div v-if="showColumnConstraintsDialog" class="modal-overlay nested-modal-overlay">
        <div class="modal nested-modal">
          <FieldConstraintsDialog
            :currentFieldType="currentColumnFieldType"
            :constraintsForm="columnConstraintsForm"
            :form="mockForm"
            :currentFieldKey="currentColumnKey"
            :currentFieldLabel="currentColumnLabel"
            :fieldDefinition="currentColumn"
            @updateConstraints="confirmColumnConstraintsDialog"
            @closeConstraintsDialog="cancelColumnConstraintsDialog"
            @showGenericDialog="forwardGenericDialog"
          />
        </div>
      </div>

      <div v-if="showTableConstraintsDialog" class="modal-overlay nested-modal-overlay">
        <div class="modal nested-modal">
          <FieldConstraintsDialog
            :currentFieldType="'table'"
            :constraintsForm="tableConstraintsForm"
            :form="formForTableVisibility"
            :currentFieldKey="value?._id || value?.name || 'table_field'"
            :currentFieldLabel="localConfig.label"
            :fieldDefinition="tableFieldDefinition"
            @updateConstraints="confirmTableConstraintsDialog"
            @closeConstraintsDialog="cancelTableConstraintsDialog"
            @showGenericDialog="forwardGenericDialog"
          />
        </div>
      </div>
    </div>

    <!-- =========================
         RENDER MODE
         ========================= -->
    <div v-else class="table-runtime-shell">
      <div class="table-runtime-wrap">
        <table class="table-runtime">
          <thead>
            <tr>
              <th v-if="resolvedTableConfig.showRowNumbers" class="rowno-col">#</th>
              <th v-for="item in runtimeColumnRenderList" :key="item.col.id || item.colIdx">
                <div class="table-head-title">
                  <span>{{ item.col.label }}</span>
                  <span v-if="item.col.constraints?.required" class="required-star">*</span>
                </div>
              </th>
              <th v-if="!readonly" class="row-actions-col">Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, rowIndex) in internalRows" :key="`row-${rowIndex}`">
              <td v-if="resolvedTableConfig.showRowNumbers" class="rowno-col">{{ rowIndex + 1 }}</td>

              <td v-for="item in runtimeColumnRenderList" :key="`${rowIndex}-${item.col.id || item.colIdx}`">
                <div
                  v-if="isRuntimeColumnVisible(item.colIdx, rowIndex)"
                  class="table-cell-box"
                >
                  <div class="table-cell-control">
                    <input
                      v-if="item.col.type === 'text'"
                      type="text"
                      v-model="internalRows[rowIndex][getRuntimeColKey(item.col, item.colIdx)]"
                      :placeholder="item.col.constraints?.placeholder || ''"
                      :readonly="readonly || !!item.col.constraints?.readonly"
                      @blur="validateCellAt(rowIndex, item.colIdx)"
                    />

                    <textarea
                      v-else-if="item.col.type === 'textarea'"
                      v-model="internalRows[rowIndex][getRuntimeColKey(item.col, item.colIdx)]"
                      :placeholder="item.col.constraints?.placeholder || ''"
                      rows="3"
                      :readonly="readonly || !!item.col.constraints?.readonly"
                      @blur="validateCellAt(rowIndex, item.colIdx)"
                    ></textarea>

                    <input
                      v-else-if="item.col.type === 'number'"
                      type="number"
                      v-model.number="internalRows[rowIndex][getRuntimeColKey(item.col, item.colIdx)]"
                      :min="item.col.constraints?.min"
                      :max="item.col.constraints?.max"
                      :step="item.col.constraints?.step"
                      :readonly="readonly || !!item.col.constraints?.readonly"
                      @blur="validateCellAt(rowIndex, item.colIdx)"
                    />

                    <DateFormatPicker
                      v-else-if="item.col.type === 'date'"
                      v-model="internalRows[rowIndex][getRuntimeColKey(item.col, item.colIdx)]"
                      :format="item.col.constraints?.dateFormat || 'dd.MM.yyyy'"
                      :placeholder="item.col.constraints?.placeholder || (item.col.constraints?.dateFormat || 'dd.MM.yyyy')"
                      :min-date="item.col.constraints?.minDate || null"
                      :max-date="item.col.constraints?.maxDate || null"
                      :readonly="readonly || !!item.col.constraints?.readonly"
                      @change="validateCellAt(rowIndex, item.colIdx)"
                      @blur="validateCellAt(rowIndex, item.colIdx)"
                    />

                    <FieldTime
                      v-else-if="item.col.type === 'time'"
                      v-model="internalRows[rowIndex][getRuntimeColKey(item.col, item.colIdx)]"
                      :placeholder="item.col.constraints?.placeholder || (item.col.constraints?.timeFormat || 'HH:mm')"
                      v-bind="item.col.constraints || {}"
                      :readonly="readonly || !!item.col.constraints?.readonly"
                      @change="validateCellAt(rowIndex, item.colIdx)"
                      @blur="validateCellAt(rowIndex, item.colIdx)"
                    />

                    <FieldSelect
                      v-else-if="item.col.type === 'select'"
                      v-model="internalRows[rowIndex][getRuntimeColKey(item.col, item.colIdx)]"
                      :options="item.col.options || []"
                      :multiple="!!item.col.constraints?.allowMultiple"
                      :allow-other="!!item.col.constraints?.allowOther"
                      :readonly="readonly || !!item.col.constraints?.readonly"
                      :default-value="item.col.constraints?.defaultValue"
                      :placeholder="'Select…'"
                      @update:modelValue="validateCellAt(rowIndex, item.colIdx)"
                    />

                    <FieldRadioGroup
                      v-else-if="item.col.type === 'radio'"
                      :name="`table_radio_${rowIndex}_${item.col.id || item.colIdx}`"
                      :options="item.col.options || []"
                      v-model="internalRows[rowIndex][getRuntimeColKey(item.col, item.colIdx)]"
                      :default-value="item.col.constraints?.defaultValue"
                      v-bind="item.col.constraints || {}"
                      :disabled="readonly || !!item.col.constraints?.readonly"
                      @change="validateCellAt(rowIndex, item.colIdx)"
                      @update:modelValue="validateCellAt(rowIndex, item.colIdx)"
                    />

                    <FieldCheckbox
                      v-else-if="item.col.type === 'checkbox'"
                      v-model="internalRows[rowIndex][getRuntimeColKey(item.col, item.colIdx)]"
                      v-bind="item.col.constraints || {}"
                      :disabled="readonly || !!item.col.constraints?.readonly"
                      @update:modelValue="validateCellAt(rowIndex, item.colIdx)"
                    />

                    <input
                      v-else
                      type="text"
                      v-model="internalRows[rowIndex][getRuntimeColKey(item.col, item.colIdx)]"
                      :placeholder="item.col.constraints?.placeholder || ''"
                      :readonly="readonly"
                      @blur="validateCellAt(rowIndex, item.colIdx)"
                    />
                  </div>

                  <div v-if="item.col.constraints?.helpText" class="cell-help">
                    {{ item.col.constraints.helpText }}
                  </div>

                  <div v-if="getCellError(rowIndex, item.colIdx)" class="table-cell-error">
                    {{ getCellError(rowIndex, item.colIdx) }}
                  </div>
                </div>
              </td>

              <td v-if="!readonly" class="row-actions-cell">
                <button
                  type="button"
                  class="btn-row-delete"
                  :disabled="internalRows.length <= 1"
                  @click="deleteRuntimeRow(rowIndex)"
                  title="Delete row"
                >
                  Delete Row
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="resolvedTableConfig.allowAddRows && !readonly" class="table-actions">
        <button type="button" class="btn-option" @click="addRuntimeRow">
          + Add Row
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import icons from "@/assets/styles/icons";
import FieldConstraintsDialog from "@/components/FieldConstraintsDialog.vue";
import DateFormatPicker from "@/components/DateFormatPicker.vue";
import FieldCheckbox from "@/components/fields/FieldCheckbox.vue";
import FieldRadioGroup from "@/components/fields/FieldRadioGroup.vue";
import FieldTime from "@/components/fields/FieldTime.vue";
import FieldSelect from "@/components/fields/FieldSelect.vue";
import { createAjv, validateFieldValue } from "@/utils/jsonschemaValidation";
import { parseDateByConfiguredFormat } from "@/utils/dateFormatParsing";

export default {
  name: "FieldTable",
  components: {
    FieldConstraintsDialog,
    DateFormatPicker,
    FieldCheckbox,
    FieldRadioGroup,
    FieldTime,
    FieldSelect
  },
  props: {
    mode: {
      type: String,
      default: "render"
    },
    value: {
      type: Object,
      default: () => ({})
    },
    modelValue: {
      type: Object,
      default: () => ({ rows: [] })
    },
    field: {
      type: Object,
      default: () => ({})
    },
    form: {
      type: Object,
      default: () => ({ sections: [] })
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  emits: ["save", "cancel", "showGenericDialog", "update:modelValue", "validation-state"],
  data() {
    return {
      icons,
      ajv: createAjv(),
      cellErrors: {},
      columnTypeOptions: [
        { label: "Text", value: "text" },
        { label: "Textarea", value: "textarea" },
        { label: "Number", value: "number" },
        { label: "Date", value: "date" },
        { label: "Time", value: "time" },
        { label: "Select", value: "select" },
        { label: "Radio", value: "radio" },
        { label: "Checkbox", value: "checkbox" }
      ],
      localConfig: this.buildInitialConfig(this.value),
      previewData: [],
      showColumnConstraintsDialog: false,
      showTableConstraintsDialog: false,
      editingColumnIndex: null,
      currentColumnFieldType: "",
      columnConstraintsForm: {},
      tableConstraintsForm: {},
      internalRows: [],
      syncingFromParent: false,
      lastEmittedRowsJson: ""
    };
  },
  computed: {
    isTable() {
      return this.type === "table";
    },
    previewRows() {
      return this.previewData;
    },

    currentColumn() {
      if (!Number.isInteger(this.editingColumnIndex)) return null;
      return this.localConfig.columns[this.editingColumnIndex] || null;
    },

    currentColumnLabel() {
      return this.currentColumn?.label || "";
    },

    currentColumnKey() {
      return this.currentColumn?.id || "";
    },

    tableFieldDefinition() {
      return {
        _id: this.value?._id || this.value?.name || "table_field",
        label: this.localConfig.label || "Table",
        name: this.value?.name || "table_field",
        type: "table",
        constraints: this.localConfig?.constraints || {}
      };
    },

    formForTableVisibility() {
      const sourceForm =
        this.form && Array.isArray(this.form.sections)
          ? JSON.parse(JSON.stringify(this.form))
          : { sections: [] };

      const tableId = String(this.value?._id || this.value?.name || "");
      const tableName = String(this.value?.name || "");

      let foundTable = false;

      sourceForm.sections = sourceForm.sections.map((section) => {
        const fields = Array.isArray(section.fields) ? section.fields : [];

        const nextFields = fields.map((field) => {
          const fieldId = String(field?._id || "");
          const fieldName = String(field?.name || "");

          if (
            tableId &&
            (fieldId === tableId || fieldName === tableId || fieldName === tableName)
          ) {
            foundTable = true;

            return {
              ...field,
              _id: this.value?._id || field._id,
              name: this.value?.name || field.name,
              label: this.localConfig.label || field.label || "Table",
              type: "table",
              constraints: JSON.parse(JSON.stringify(this.localConfig?.constraints || {})),
              tableConfig: {
                ...(field.tableConfig || {}),
                ...(this.value?.tableConfig || {}),
                columns: JSON.parse(JSON.stringify(this.localConfig?.columns || []))
              }
            };
          }

          return field;
        });

        return {
          ...section,
          fields: nextFields
        };
      });

      if (!foundTable && sourceForm.sections.length) {
        sourceForm.sections[0].fields = [
          ...(sourceForm.sections[0].fields || []),
          this.tableFieldDefinition
        ];
      }

      return sourceForm;
    },

    mockForm() {
      return {
        sections: [
          {
            title: "Table",
            fields: this.localConfig.columns.map(col => ({
              _id: col.id,
              label: col.label,
              name: col.id,
              type: col.type,
              options: Array.isArray(col.options) ? col.options : [],
              constraints: col.constraints || {}
            }))
          }
        ]
      };
    },

    resolvedTableConfig() {
      return {
        version: 1,
        mode: "2d",
        initialRows: Number(this.field?.tableConfig?.initialRows) || 1,
        allowAddRows: this.field?.tableConfig?.allowAddRows !== false,
        showRowNumbers: this.field?.tableConfig?.showRowNumbers !== false
      };
    },

    resolvedColumns() {
      return Array.isArray(this.field?.tableConfig?.columns)
        ? this.field.tableConfig.columns
        : [];
    },

    previewColumnRenderList() {
      return this.localConfig.columns
        .map((col, colIdx) => ({ col, colIdx }))
        .filter(({ colIdx }) => this.shouldRenderPreviewColumn(colIdx));
    },

    runtimeColumnRenderList() {
      return this.resolvedColumns
        .map((col, colIdx) => ({ col, colIdx }))
        .filter(({ colIdx }) => this.shouldRenderRuntimeColumn(colIdx));
    }
  },
  watch: {
    value: {
      deep: true,
      handler(next) {
        if (this.mode !== "configure") return;
        this.localConfig = this.buildInitialConfig(next);
        this.ensurePreviewData();
      }
    },

    localConfig: {
      deep: true,
      handler() {
        if (this.mode !== "configure") return;
        this.ensurePreviewData();
      }
    },

    modelValue: {
      deep: true,
      immediate: true,
      handler(next) {
        if (this.mode !== "render") return;

        const normalized = this.normalizeRows(
          next?.rows,
          this.resolvedColumns,
          this.resolvedTableConfig.initialRows
        );

        const nextJson = JSON.stringify(normalized);
        const currentJson = JSON.stringify(this.internalRows);

        if (nextJson === currentJson) return;

        this.syncingFromParent = true;
        this.internalRows = normalized;
        this.lastEmittedRowsJson = nextJson;

        this.$nextTick(() => {
          this.syncingFromParent = false;
          this.validateAllCells();
        });
      }
    },

    field: {
      deep: true,
      immediate: true,
      handler() {
        if (this.mode !== "render") return;

        const normalized = this.normalizeRows(
          this.modelValue?.rows,
          this.resolvedColumns,
          this.resolvedTableConfig.initialRows
        );

        const nextJson = JSON.stringify(normalized);
        const currentJson = JSON.stringify(this.internalRows);

        if (nextJson === currentJson) return;

        this.syncingFromParent = true;
        this.internalRows = normalized;
        this.lastEmittedRowsJson = nextJson;

        this.$nextTick(() => {
          this.syncingFromParent = false;
          this.validateAllCells();
        });
      }
    },

    internalRows: {
      deep: true,
      handler(rows) {
        if (this.mode !== "render") return;
        if (this.syncingFromParent) return;

        const payload = { rows: JSON.parse(JSON.stringify(rows)) };
        const nextJson = JSON.stringify(payload.rows);

        if (nextJson === this.lastEmittedRowsJson) return;

        this.lastEmittedRowsJson = nextJson;
        this.$emit("update:modelValue", payload);

        this.$nextTick(() => {
          this.validateAllCells();
        });
      }
    }
  },
  mounted() {
    if (this.mode === "configure") {
      this.ensurePreviewData();
    }

    if (this.mode === "render") {
      const normalized = this.normalizeRows(
        this.modelValue?.rows,
        this.resolvedColumns,
        this.resolvedTableConfig.initialRows
      );
      this.internalRows = normalized;
      this.lastEmittedRowsJson = JSON.stringify(normalized);

      this.$nextTick(() => {
        this.validateAllCells();
      });
    }
  },
  methods: {
    validateForSubmit() {
      return this.validateAllCells();
    },

    buildInitialConfig(src) {
      const initialColumns =
        Array.isArray(src?.tableConfig?.columns) && src.tableConfig.columns.length
          ? src.tableConfig.columns.map((c, idx) => this.makeColumn(idx, c))
          : [this.makeColumn(0, { label: "Column 1", type: "text" })];

      return {
        label: src?.label || "Table",
        initialRows: Number(src?.tableConfig?.initialRows) > 0 ? Number(src.tableConfig.initialRows) : 1,
        allowAddRows: src?.tableConfig?.allowAddRows !== false,
        showRowNumbers: src?.tableConfig?.showRowNumbers !== false,
        constraints: {
          helpText: src?.constraints?.helpText || "",
          required: !!src?.constraints?.required,
          readonly: !!src?.constraints?.readonly,
          visibilityLogic: src?.constraints?.visibilityLogic || {
            action: "show",
            match: "all",
            rules: []
          }
        },
        columns: initialColumns
      };
    },

    makeColumn(idx, c = {}) {
      const type = this.normalizeAllowedType(c.type);

      let options = Array.isArray(c.options) ? [...c.options] : [];
      options = options.map(o => String(o || "").trim()).filter(Boolean);

      if ((type === "select" || type === "radio") && !options.length) {
        options = ["Option 1"];
      }

      return {
        id: c.id || `table_col_${Date.now()}_${idx}_${Math.random().toString(16).slice(2, 8)}`,
        key: c.key || this.buildColumnKey(c.label || `Column ${idx + 1}`, idx),
        label: c.label || `Column ${idx + 1}`,
        type,
        options,
        constraints: {
          ...(c.constraints || {})
        }
      };
    },

    normalizeAllowedType(type) {
      const allowed = ["text", "textarea", "number", "date", "time", "select", "radio", "checkbox"];
      return allowed.includes(type) ? type : "text";
    },

    prettyType(type) {
      const map = {
        text: "Text",
        textarea: "Textarea",
        number: "Number",
        date: "Date",
        time: "Time",
        select: "Select",
        radio: "Radio",
        checkbox: "Checkbox"
      };
      return map[type] || "Text";
    },

    buildColumnKey(label, idx) {
      const base = String(label || `column_${idx + 1}`)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      return base || `column_${idx + 1}`;
    },

    getPreviewColKey(col, idx) {
      return col?.id || col?.key || this.buildColumnKey(col?.label, idx);
    },

    getRuntimeColKey(col, idx) {
      return col?.id || col?.key || this.buildColumnKey(col?.label, idx);
    },

    defaultValueForType(type, constraints = {}) {
      if (Object.prototype.hasOwnProperty.call(constraints || {}, "defaultValue")) {
        return constraints.defaultValue;
      }

      switch (type) {
        case "number":
          return null;
        case "checkbox":
          return false;
        case "select":
          return constraints?.allowMultiple ? [] : "";
        case "radio":
          return "";
        default:
          return "";
      }
    },

    ensurePreviewData() {
      const columns = this.localConfig.columns.map((col, idx) => ({
        ...col,
        key: this.getPreviewColKey(col, idx)
      }));

      const count = Math.max(1, Number(this.localConfig.initialRows) || 1);
      const rows = [];

      for (let i = 0; i < count; i += 1) {
        const row = {};
        columns.forEach((col) => {
          row[col.key] = this.defaultValueForType(col.type, col.constraints || {});
        });
        rows.push(row);
      }

      this.previewData = rows;
    },

    normalizeRows(rows, columns, initialRows = 1) {
      const safeColumns = Array.isArray(columns) ? columns : [];
      const incoming = Array.isArray(rows) ? JSON.parse(JSON.stringify(rows)) : [];
      const result = [];

      if (!incoming.length) {
        const count = Math.max(1, Number(initialRows) || 1);
        for (let i = 0; i < count; i += 1) {
          result.push(this.createEmptyRow(safeColumns));
        }
        return result;
      }

      incoming.forEach((row) => {
        const normalized = {};
        safeColumns.forEach((col, idx) => {
          const key = this.getRuntimeColKey(col, idx);
          normalized[key] =
            row?.[key] !== undefined
              ? row[key]
              : this.defaultValueForType(col.type, col.constraints || {});
        });
        result.push(normalized);
      });

      return result;
    },

    createEmptyRow(columns) {
      const row = {};
      (columns || []).forEach((col, idx) => {
        const key = this.getRuntimeColKey(col, idx);
        row[key] = this.defaultValueForType(col.type, col.constraints || {});
      });
      return row;
    },

    getNormalizedVisibilityLogic(rawLogic) {
      const logic = rawLogic && typeof rawLogic === "object" ? rawLogic : {};
      const rules = Array.isArray(logic.rules) ? logic.rules.filter(Boolean) : [];

      return {
        action: String(logic.action || "show").toLowerCase() === "hide" ? "hide" : "show",
        match: String(logic.match || "all").toLowerCase() === "any" ? "any" : "all",
        rules
      };
    },

    isBlankValue(val) {
      if (val === null || val === undefined) return true;
      if (typeof val === "string") return val.trim() === "";
      if (Array.isArray(val)) return val.length === 0;
      return false;
    },

    toComparableNumber(val) {
      if (val === null || val === undefined || val === "") return null;
      const n = Number(val);
      return Number.isFinite(n) ? n : null;
    },

    parseDateLike(value, format = "dd.MM.yyyy") {
      const parsed = parseDateByConfiguredFormat(value, format);
      return parsed ? parsed.getTime() : null;
    },

    parseTimeLike(value) {
      if (!value) return null;
      const s = String(value).trim();
      const mm = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(s);
      if (!mm) return null;

      const h = +mm[1];
      const m = +mm[2];
      const sec = mm[3] ? +mm[3] : 0;

      return h * 3600 + m * 60 + sec;
    },

    compareArraysContainsAll(leftArr, rightArr) {
      if (!Array.isArray(leftArr) || !Array.isArray(rightArr)) return false;
      return rightArr.every((v) => leftArr.includes(v));
    },

    compareChoiceValue(sourceValue, compareValue, operator) {
      const op = String(operator || "eq").toLowerCase();

      const leftValues = Array.isArray(sourceValue)
        ? sourceValue.map((v) => String(v ?? "").trim()).filter(Boolean)
        : [String(sourceValue ?? "").trim()].filter(Boolean);

      const rightValues = Array.isArray(compareValue)
        ? compareValue.map((v) => String(v ?? "").trim()).filter(Boolean)
        : [String(compareValue ?? "").trim()].filter(Boolean);

      if (op === "eq") {
        return rightValues.some((v) => leftValues.includes(v));
      }

      if (op === "neq") {
        return !rightValues.some((v) => leftValues.includes(v));
      }

      if (op === "contains") {
        return rightValues.some((v) => leftValues.includes(v));
      }

      if (op === "not_contains") {
        return !rightValues.some((v) => leftValues.includes(v));
      }

      return false;
    },

    getColumnBySourceKey(columns, sourceKey) {
      const key = String(sourceKey || "");
      if (!key) return null;

      for (let idx = 0; idx < (columns || []).length; idx += 1) {
        const col = columns[idx];
        const candidates = [
          col?.id,
          col?.key,
          this.buildColumnKey(col?.label, idx),
          col?.label
        ].filter(Boolean).map(String);

        if (candidates.includes(key)) {
          return { col, colIdx: idx };
        }
      }

      return null;
    },

    getRowValueForColumn(row, columns, sourceKey) {
      const found = this.getColumnBySourceKey(columns, sourceKey);
      if (!found) return undefined;

      const rowKey = this.mode === "configure"
        ? this.getPreviewColKey(found.col, found.colIdx)
        : this.getRuntimeColKey(found.col, found.colIdx);

      return row?.[rowKey];
    },

    evaluateColumnVisibilityRule(rule, row, columns) {
      const operator = String(rule?.operator || "eq").toLowerCase();
      const sourceInfo = this.getColumnBySourceKey(columns, rule?.sourceFieldKey);
      if (!sourceInfo) return false;

      const sourceField = sourceInfo.col;
      const sourceValue = this.getRowValueForColumn(row, columns, rule?.sourceFieldKey);
      const sourceType = String(sourceField?.type || "").toLowerCase();
      const constraints = sourceField?.constraints || {};
      const compareValue = rule?.value;
      const compareValueTo = rule?.valueTo;

      if (operator === "empty" || operator === "is_empty") return this.isBlankValue(sourceValue);
      if (operator === "not_empty" || operator === "is_not_empty") return !this.isBlankValue(sourceValue);

      if (sourceType === "select" || sourceType === "radio") {
        return this.compareChoiceValue(sourceValue, compareValue, operator);
      }

      if (sourceType === "checkbox") {
        const left = !!sourceValue;
        const right = compareValue === true || compareValue === "true" || compareValue === 1 || compareValue === "1";
        if (operator === "eq") return left === right;
        if (operator === "neq") return left !== right;
      }

      if (sourceType === "number") {
        const left = this.toComparableNumber(sourceValue);
        const right = this.toComparableNumber(compareValue);
        const rightTo = this.toComparableNumber(compareValueTo);

        if (left === null) return false;

        if (operator === "eq") return right !== null && left === right;
        if (operator === "neq") return right !== null && left !== right;
        if (operator === "lt") return right !== null && left < right;
        if (operator === "lte") return right !== null && left <= right;
        if (operator === "gt") return right !== null && left > right;
        if (operator === "gte") return right !== null && left >= right;
        if (operator === "between") return right !== null && rightTo !== null && left >= right && left <= rightTo;
      }

      if (sourceType === "date") {
        const fmt = constraints.dateFormat || "dd.MM.yyyy";
        const left = this.parseDateLike(sourceValue, fmt);
        const right = this.parseDateLike(compareValue, fmt);
        const rightTo = this.parseDateLike(compareValueTo, fmt);

        if (left === null) return false;

        if (operator === "eq") return right !== null && left === right;
        if (operator === "neq") return right !== null && left !== right;
        if (operator === "lt") return right !== null && left < right;
        if (operator === "lte") return right !== null && left <= right;
        if (operator === "gt") return right !== null && left > right;
        if (operator === "gte") return right !== null && left >= right;
        if (operator === "between") return right !== null && rightTo !== null && left >= right && left <= rightTo;
      }

      if (sourceType === "time") {
        const left = this.parseTimeLike(sourceValue);
        const right = this.parseTimeLike(compareValue);
        const rightTo = this.parseTimeLike(compareValueTo);

        if (left === null) return false;

        if (operator === "eq") return right !== null && left === right;
        if (operator === "neq") return right !== null && left !== right;
        if (operator === "lt") return right !== null && left < right;
        if (operator === "lte") return right !== null && left <= right;
        if (operator === "gt") return right !== null && left > right;
        if (operator === "gte") return right !== null && left >= right;
        if (operator === "between") return right !== null && rightTo !== null && left >= right && left <= rightTo;
      }

      const leftText = String(sourceValue ?? "");
      const rightText = String(compareValue ?? "");
      const rightToText = String(compareValueTo ?? "");

      if (operator === "eq") return leftText === rightText;
      if (operator === "neq") return leftText !== rightText;
      if (operator === "contains") return leftText.includes(rightText);
      if (operator === "not_contains") return !leftText.includes(rightText);
      if (operator === "starts_with") return leftText.startsWith(rightText);
      if (operator === "ends_with") return leftText.endsWith(rightText);
      if (operator === "regex") {
        try {
          return new RegExp(rightText).test(leftText);
        } catch {
          return false;
        }
      }
      if (operator === "between") return leftText >= rightText && leftText <= rightToText;

      return false;
    },

    evaluateColumnVisibility(col, row, columns) {
      const logic = this.getNormalizedVisibilityLogic(col?.constraints?.visibilityLogic);
      if (!logic.rules.length) return true;

      const results = logic.rules.map((rule) =>
        this.evaluateColumnVisibilityRule(rule, row, columns)
      );

      const matched = logic.match === "any"
        ? results.some(Boolean)
        : results.every(Boolean);

      return logic.action === "hide" ? !matched : matched;
    },

    isPreviewColumnVisible(colIdx, rowIndex) {
      const row = this.previewRows?.[rowIndex] || {};
      const col = this.localConfig.columns?.[colIdx];
      if (!col) return false;
      return this.evaluateColumnVisibility(col, row, this.localConfig.columns || []);
    },

    isRuntimeColumnVisible(colIdx, rowIndex) {
      const row = this.internalRows?.[rowIndex] || {};
      const col = this.resolvedColumns?.[colIdx];
      if (!col) return false;
      return this.evaluateColumnVisibility(col, row, this.resolvedColumns || []);
    },

    shouldRenderPreviewColumn(colIdx) {
      const rows = Array.isArray(this.previewRows) ? this.previewRows : [];
      if (!rows.length) return true;
      return rows.some((_, rowIndex) => this.isPreviewColumnVisible(colIdx, rowIndex));
    },

    shouldRenderRuntimeColumn(colIdx) {
      const rows = Array.isArray(this.internalRows) ? this.internalRows : [];
      if (!rows.length) return true;
      return rows.some((_, rowIndex) => this.isRuntimeColumnVisible(colIdx, rowIndex));
    },

    cellErrorKey(rowIndex, colIndex) {
      return `${rowIndex}-${colIndex}`;
    },

    getCellError(rowIndex, colIndex) {
      return this.cellErrors[this.cellErrorKey(rowIndex, colIndex)] || "";
    },

    setCellError(rowIndex, colIndex, message) {
      const key = this.cellErrorKey(rowIndex, colIndex);
      const next = { ...this.cellErrors };
      if (message) next[key] = message;
      else delete next[key];
      this.cellErrors = next;
    },

    clearCellError(rowIndex, colIndex) {
      this.setCellError(rowIndex, colIndex, "");
    },

    isCellEmpty(col, value) {
      if (col.type === "checkbox") return value !== true;
      if (Array.isArray(value)) return value.length === 0;
      return value == null || (typeof value === "string" && value.trim() === "");
    },

    validateDateBounds(value, constraints, label) {
      if (!value) return "";

      const parse = (s, fmt) => {
        const str = String(s || "");
        let m;

        switch (fmt) {
          case "dd.MM.yyyy":
            m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(str);
            return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null;
          case "MM-dd-yyyy":
            m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(str);
            return m ? new Date(+m[3], +m[1] - 1, +m[2]) : null;
          case "dd-MM-yyyy":
            m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(str);
            return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null;
          case "yyyy-MM-dd":
            m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
            return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
          case "MM/yyyy":
            m = /^(\d{2})\/(\d{4})$/.exec(str);
            return m ? new Date(+m[2], +m[1] - 1, 1) : null;
          case "MM-yyyy":
            m = /^(\d{2})-(\d{4})$/.exec(str);
            return m ? new Date(+m[2], +m[1] - 1, 1) : null;
          case "yyyy/MM":
            m = /^(\d{4})\/(\d{2})$/.exec(str);
            return m ? new Date(+m[1], +m[2] - 1, 1) : null;
          case "yyyy-MM":
            m = /^(\d{4})-(\d{2})$/.exec(str);
            return m ? new Date(+m[1], +m[2] - 1, 1) : null;
          case "yyyy":
            m = /^(\d{4})$/.exec(str);
            return m ? new Date(+m[1], 0, 1) : null;
          default:
            return null;
        }
      };

      const fmt = constraints?.dateFormat || "dd.MM.yyyy";
      const current = parse(value, fmt);
      if (!current) return "";

      if (constraints?.minDate) {
        const minD = parse(constraints.minDate, fmt);
        if (minD && current < minD) {
          return `${label} must be ≥ ${constraints.minDate}.`;
        }
      }

      if (constraints?.maxDate) {
        const maxD = parse(constraints.maxDate, fmt);
        if (maxD && current > maxD) {
          return `${label} must be ≤ ${constraints.maxDate}.`;
        }
      }

      return "";
    },

    validateTimeBounds(value, constraints, label) {
      if (!value) return "";

      const toSec = (s) => {
        const m = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(s || ""));
        if (!m) return null;
        return (+m[1] * 3600) + (+m[2] * 60) + (m[3] ? +m[3] : 0);
      };

      const cur = toSec(value);
      if (cur == null) return "";

      if (constraints?.minTime) {
        const minT = toSec(constraints.minTime);
        if (minT != null && cur < minT) {
          return `${label} must be ≥ ${constraints.minTime}.`;
        }
      }

      if (constraints?.maxTime) {
        const maxT = toSec(constraints.maxTime);
        if (maxT != null && cur > maxT) {
          return `${label} must be ≤ ${constraints.maxTime}.`;
        }
      }

      return "";
    },

    validateSingleCell(rowIndex, colIndex) {
      const col = this.resolvedColumns[colIndex];
      if (!col) return true;

      if (!this.isRuntimeColumnVisible(colIndex, rowIndex)) {
        this.clearCellError(rowIndex, colIndex);
        return true;
      }

      const key = this.getRuntimeColKey(col, colIndex);
      const row = this.internalRows[rowIndex] || {};
      const value = row[key];
      const label = col.label || `Column ${colIndex + 1}`;
      const constraints = col.constraints || {};

      this.clearCellError(rowIndex, colIndex);

      if (constraints.required && this.isCellEmpty(col, value)) {
        this.setCellError(rowIndex, colIndex, `${label} is required.`);
        return false;
      }

      if (constraints.readonly) return true;

      const pseudoField = {
        type: col.type,
        label,
        name: key,
        options: Array.isArray(col.options) ? col.options : [],
        constraints
      };

      if (col.type !== "file") {
        const { valid, message } = validateFieldValue(this.ajv, pseudoField, value);
        if (!valid) {
          this.setCellError(rowIndex, colIndex, message || `${label} is invalid.`);
          return false;
        }
      }

      if (col.type === "number" && value !== "" && value != null) {
        const num = Number(value);
        if (!Number.isFinite(num)) {
          this.setCellError(rowIndex, colIndex, `${label} must be a number.`);
          return false;
        }

        if (constraints.integerOnly && !Number.isInteger(num)) {
          this.setCellError(rowIndex, colIndex, `${label} must be an integer.`);
          return false;
        }

        const integerPart = String(Math.trunc(Math.abs(num)));
        const digits = integerPart.length;

        if (Number.isFinite(Number(constraints.minDigits)) && digits < Number(constraints.minDigits)) {
          this.setCellError(rowIndex, colIndex, `${label} must have at least ${constraints.minDigits} digits.`);
          return false;
        }

        if (Number.isFinite(Number(constraints.maxDigits)) && digits > Number(constraints.maxDigits)) {
          this.setCellError(rowIndex, colIndex, `${label} must have at most ${constraints.maxDigits} digits.`);
          return false;
        }
      }

      if (col.type === "date") {
        const msg = this.validateDateBounds(value, constraints, label);
        if (msg) {
          this.setCellError(rowIndex, colIndex, msg);
          return false;
        }
      }

      if (col.type === "time") {
        const msg = this.validateTimeBounds(value, constraints, label);
        if (msg) {
          this.setCellError(rowIndex, colIndex, msg);
          return false;
        }
      }

      if ((col.type === "select" || col.type === "radio") && !constraints.allowOther && value != null && value !== "") {
        const opts = Array.isArray(col.options) ? col.options.map(String) : [];
        if (Array.isArray(value)) {
          const allValid = value.every((v) => opts.includes(String(v)));
          if (!allValid) {
            this.setCellError(rowIndex, colIndex, `${label} has an invalid option.`);
            return false;
          }
        } else if (!opts.includes(String(value))) {
          this.setCellError(rowIndex, colIndex, `${label} has an invalid option.`);
          return false;
        }
      }

      return true;
    },

    validateCellAt(rowIndex, colIndex) {
      const ok = this.validateSingleCell(rowIndex, colIndex);
      this.emitValidationState();
      return ok;
    },

    validateAllCells() {
      let ok = true;

      for (let r = 0; r < this.internalRows.length; r += 1) {
        for (let c = 0; c < this.resolvedColumns.length; c += 1) {
          const valid = this.validateSingleCell(r, c);
          if (!valid) ok = false;
        }
      }

      this.emitValidationState();
      return ok;
    },

    emitValidationState() {
      const messages = Object.values(this.cellErrors || {}).filter(Boolean);
      this.$emit("validation-state", {
        valid: messages.length === 0,
        message: messages[0] || "",
        cellErrors: { ...this.cellErrors }
      });
    },

    addColumn() {
      this.localConfig.columns.push(
        this.makeColumn(this.localConfig.columns.length, {
          label: `Column ${this.localConfig.columns.length + 1}`,
          type: "text"
        })
      );
    },

    removeColumn(idx) {
      if (this.localConfig.columns.length <= 1) return;
      this.localConfig.columns.splice(idx, 1);
    },

    moveColumn(idx, dir) {
      const next = idx + dir;
      if (next < 0 || next >= this.localConfig.columns.length) return;
      const moved = this.localConfig.columns.splice(idx, 1)[0];
      this.localConfig.columns.splice(next, 0, moved);
    },

    onTypeChanged(col) {
      col.type = this.normalizeAllowedType(col.type);

      if (col.type !== "select" && col.type !== "radio") {
        col.options = [];
      } else {
        const cleaned = Array.isArray(col.options)
          ? col.options.map(o => String(o || "").trim()).filter(Boolean)
          : [];

        col.options = cleaned.length ? cleaned : ["Option 1"];
      }

      col.constraints = this.rebuildColumnConstraintsForType(col);
    },

    rebuildColumnConstraintsForType(col) {
      const prev = JSON.parse(JSON.stringify(col.constraints || {}));
      const type = col.type;

      if (type === "checkbox") {
        return {
          helpText: prev.helpText || "",
          required: !!prev.required,
          readonly: !!prev.readonly,
          visibilityLogic: prev.visibilityLogic || {
            action: "show",
            match: "all",
            rules: []
          }
        };
      }

      if (type === "select" || type === "radio") {
        return {
          helpText: prev.helpText || "",
          required: !!prev.required,
          readonly: !!prev.readonly,
          allowMultiple: type === "radio" ? !!prev.allowMultiple : false,
          defaultValue: prev.defaultValue,
          visibilityLogic: prev.visibilityLogic || {
            action: "show",
            match: "all",
            rules: []
          }
        };
      }

      if (type === "date") {
        return {
          helpText: prev.helpText || "",
          required: !!prev.required,
          readonly: !!prev.readonly,
          placeholder: prev.placeholder || "",
          dateFormat: prev.dateFormat || "dd.MM.yyyy",
          minDate: prev.minDate || null,
          maxDate: prev.maxDate || null,
          defaultValue: prev.defaultValue,
          visibilityLogic: prev.visibilityLogic || {
            action: "show",
            match: "all",
            rules: []
          }
        };
      }

      if (type === "time") {
        return {
          helpText: prev.helpText || "",
          required: !!prev.required,
          readonly: !!prev.readonly,
          placeholder: prev.placeholder || "",
          hourCycle: prev.hourCycle || "24",
          defaultValue: prev.defaultValue,
          visibilityLogic: prev.visibilityLogic || {
            action: "show",
            match: "all",
            rules: []
          }
        };
      }

      if (type === "number") {
        return {
          helpText: prev.helpText || "",
          required: !!prev.required,
          readonly: !!prev.readonly,
          placeholder: prev.placeholder || "",
          min: prev.min,
          max: prev.max,
          step: prev.step,
          integerOnly: !!prev.integerOnly,
          minDigits: prev.minDigits,
          maxDigits: prev.maxDigits,
          defaultValue: prev.defaultValue,
          visibilityLogic: prev.visibilityLogic || {
            action: "show",
            match: "all",
            rules: []
          }
        };
      }

      return {
        helpText: prev.helpText || "",
        required: !!prev.required,
        readonly: !!prev.readonly,
        placeholder: prev.placeholder || "",
        defaultValue: prev.defaultValue,
        visibilityLogic: prev.visibilityLogic || {
          action: "show",
          match: "all",
          rules: []
        }
      };
    },

    buildConstraintsFormForColumn(col) {
      const existing = JSON.parse(JSON.stringify(col.constraints || {}));

      return {
        ...existing,
        type: col.type,
        options: (col.type === "select" || col.type === "radio")
          ? (Array.isArray(col.options) ? [...col.options] : [])
          : existing.options,
        dateFormat: col.type === "date"
          ? (existing.dateFormat || "dd.MM.yyyy")
          : existing.dateFormat
      };
    },

    buildConstraintsFormForTable() {
      const existing = JSON.parse(JSON.stringify(this.localConfig?.constraints || {}));
      return {
        ...existing,
        helpText: existing.helpText || "",
        required: !!existing.required,
        readonly: !!existing.readonly,
        visibilityLogic: existing.visibilityLogic || {
          action: "show",
          match: "all",
          rules: []
        }
      };
    },

    openTableSettings() {
      this.tableConstraintsForm = this.buildConstraintsFormForTable();
      this.showTableConstraintsDialog = true;
    },

    confirmTableConstraintsDialog(payload) {
      const nextField = payload?.field || null;
      if (!nextField) {
        this.showTableConstraintsDialog = false;
        return;
      }

      this.localConfig.constraints = {
        ...(this.localConfig.constraints || {}),
        ...(nextField.constraints || {}),
        visibilityLogic:
          nextField?.constraints?.visibilityLogic ||
          this.localConfig?.constraints?.visibilityLogic || {
            action: "show",
            match: "all",
            rules: []
          }
      };

      this.showTableConstraintsDialog = false;
    },

    cancelTableConstraintsDialog() {
      this.showTableConstraintsDialog = false;
    },

    openColumnSettings(idx) {
      const col = this.localConfig.columns[idx];
      if (!col) return;

      this.editingColumnIndex = idx;
      this.currentColumnFieldType = col.type;
      this.columnConstraintsForm = this.buildConstraintsFormForColumn(col);
      this.showColumnConstraintsDialog = true;
    },

    confirmColumnConstraintsDialog(payload) {
      const col = this.currentColumn;
      if (!col) {
        this.showColumnConstraintsDialog = false;
        this.editingColumnIndex = null;
        return;
      }

      const nextField = payload?.field || null;
      const nextType = String(payload?.type || col.type || "text").toLowerCase();

      if (!nextField) {
        this.showColumnConstraintsDialog = false;
        this.editingColumnIndex = null;
        return;
      }

      col.type = this.normalizeAllowedType(nextType);

      col.id = nextField.id || col.id;
      col.label = nextField.label || col.label || `Column ${(this.editingColumnIndex ?? 0) + 1}`;
      col.key = col.id || nextField.key || col.key || this.buildColumnKey(col.label, this.editingColumnIndex || 0);

      if (col.type === "select" || col.type === "radio") {
        const cleanedOptions = Array.isArray(nextField.options)
          ? nextField.options.map((o) => String(o || "").trim()).filter(Boolean)
          : [];

        col.options = cleanedOptions.length ? Array.from(new Set(cleanedOptions)) : ["Option 1"];
      } else {
        col.options = [];
      }

      col.constraints = JSON.parse(JSON.stringify(nextField.constraints || {}));

      if (col.type === "select" && col.constraints) {
        delete col.constraints.allowMultiple;
      }

      this.currentColumnFieldType = col.type;
      this.columnConstraintsForm = this.buildConstraintsFormForColumn(col);
      this.showColumnConstraintsDialog = false;
      this.editingColumnIndex = null;
    },

    cancelColumnConstraintsDialog() {
      this.showColumnConstraintsDialog = false;
      this.editingColumnIndex = null;
    },

    forwardGenericDialog(msg) {
      this.$emit("showGenericDialog", msg);
    },

    tableSummary() {
      const c = this.localConfig?.constraints || {};
      const parts = [];

      if (c.required) parts.push("Required");
      if (c.readonly) parts.push("Read only");
      if (c.helpText) parts.push("Help text set");

      const rules = Array.isArray(c?.visibilityLogic?.rules) ? c.visibilityLogic.rules : [];
      if (rules.length) {
        parts.push(`Visibility: ${c.visibilityLogic?.action || "show"} (${rules.length} rule${rules.length === 1 ? "" : "s"})`);
      }

      return parts.join(" • ");
    },

    columnSummary(col) {
      const c = col.constraints || {};
      const parts = [];

      if (c.required) parts.push("Required");
      if (c.readonly) parts.push("Read only");

      if ((col.type === "text" || col.type === "textarea") && c.placeholder) {
        parts.push("Placeholder set");
      }

      if (col.type === "number") {
        if (c.min !== undefined && c.min !== null) parts.push(`Min ${c.min}`);
        if (c.max !== undefined && c.max !== null) parts.push(`Max ${c.max}`);
        if (c.step !== undefined && c.step !== null) parts.push(`Step ${c.step}`);
        if (c.integerOnly) parts.push("Integer only");
      }

      if (col.type === "date" && c.dateFormat) {
        parts.push(`Format ${c.dateFormat}`);
      }

      if (col.type === "time" && c.hourCycle) {
        parts.push(`${c.hourCycle}-hour`);
      }

      if ((col.type === "select" || col.type === "radio") && Array.isArray(col.options) && col.options.length) {
        parts.push(`${col.options.length} option${col.options.length === 1 ? "" : "s"}`);
      }

      if (c.helpText) parts.push("Help text set");
      if (Object.prototype.hasOwnProperty.call(c, "defaultValue")) parts.push("Default set");

      const rules = Array.isArray(c?.visibilityLogic?.rules) ? c.visibilityLogic.rules : [];
      if (rules.length) {
        parts.push(`Visibility: ${c.visibilityLogic?.action || "show"} (${rules.length} rule${rules.length === 1 ? "" : "s"})`);
      }

      return parts.join(" • ");
    },

    validateConfig() {
      if (!this.localConfig.label) {
        return "Please enter a table field label.";
      }

      if (!Number.isFinite(this.localConfig.initialRows) || this.localConfig.initialRows < 1) {
        return "Initial rows must be at least 1.";
      }

      if (!Array.isArray(this.localConfig.columns) || !this.localConfig.columns.length) {
        return "Please add at least one column.";
      }

      const seenLabels = new Set();

      for (let i = 0; i < this.localConfig.columns.length; i += 1) {
        const col = this.localConfig.columns[i];

        if (!col.label || !String(col.label).trim()) {
          return `Column ${i + 1} needs a label.`;
        }

        const key = String(col.label).trim().toLowerCase();
        if (seenLabels.has(key)) {
          return `Duplicate column label "${col.label}" found.`;
        }
        seenLabels.add(key);

        if (
          (col.type === "select" || col.type === "radio") &&
          (!Array.isArray(col.options) || !col.options.length)
        ) {
          return `Column "${col.label}" needs at least one option. Open its settings and add options.`;
        }
      }

      return "";
    },

    saveConfig() {
      const err = this.validateConfig();
      if (err) {
        this.$emit("showGenericDialog", err);
        return;
      }

      const columns = this.localConfig.columns.map((col, idx) => ({
        id: col.id,
        key: col.id || col.key || this.buildColumnKey(col.label, idx),
        label: col.label,
        type: col.type,
        options: Array.isArray(col.options) ? [...col.options] : [],
        constraints: (() => {
          const cloned = JSON.parse(JSON.stringify(col.constraints || {}));
          if (col.type === "select") delete cloned.allowMultiple;
          return cloned;
        })()
      }));

      const rows = [];
      const rowCount = Math.max(1, Number(this.localConfig.initialRows) || 1);

      for (let r = 0; r < rowCount; r += 1) {
        const row = {};
        columns.forEach((col) => {
          row[col.key] = this.defaultValueForType(col.type, col.constraints || {});
        });
        rows.push(row);
      }

      const payload = {
        _id: this.value?._id,
        label: this.localConfig.label,
        name: this.value?.name || `table_${Date.now()}`,
        type: "table",
        value: { rows },
        constraints: {
          helpText: this.localConfig?.constraints?.helpText || "",
          required: !!this.localConfig?.constraints?.required,
          readonly: !!this.localConfig?.constraints?.readonly,
          visibilityLogic: this.localConfig?.constraints?.visibilityLogic || {
            action: "show",
            match: "all",
            rules: []
          }
        },
        tableConfig: {
          version: 1,
          mode: "2d",
          initialRows: Number(this.localConfig.initialRows),
          allowAddRows: !!this.localConfig.allowAddRows,
          showRowNumbers: !!this.localConfig.showRowNumbers,
          columns
        }
      };

      this.$emit("save", { ok: true, payload });
    },

    addRuntimeRow() {
      this.internalRows.push(this.createEmptyRow(this.resolvedColumns));
      this.$nextTick(() => {
        this.validateAllCells();
      });
    },

    deleteRuntimeRow(rowIndex) {
      if (this.internalRows.length <= 1) return;
      this.internalRows.splice(rowIndex, 1);

      const rebuilt = {};
      this.internalRows.forEach((_, rIdx) => {
        this.resolvedColumns.forEach((__, cIdx) => {
          const msg = this.getCellError(
            rIdx >= rowIndex ? rIdx + 1 : rIdx,
            cIdx
          );
          if (msg) {
            rebuilt[`${rIdx}-${cIdx}`] = msg;
          }
        });
      });
      this.cellErrors = rebuilt;

      this.$nextTick(() => {
        this.validateAllCells();
      });
    }
  }
};
</script>

<style scoped lang="scss">
.field-table-root {
  width: 100%;
  overflow: visible;
}

/* =========================
   CONFIGURE MODE
   ========================= */
.table-configurator {
  width: min(92vw, 980px);
  max-width: 980px;
  min-width: 0;
  margin: 0 auto;
  overflow: visible;
}

.tc-header {
  margin-bottom: 18px;
}

.tc-title {
  text-align: center;
  margin: 0;
}

.tc-subtitle {
  margin: 8px auto 0;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
  max-width: 760px;
  line-height: 1.45;
}

.tc-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.tc-column-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 12px;
}

.tc-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.tc-group label {
  font-weight: 600;
  font-size: 13px;
  color: #111827;
}

.tc-group input,
.tc-group select {
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1.2;
  margin: 0;
}

.tc-group input:focus,
.tc-group select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.tc-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}

.tc-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #111827;
}

.tc-checkbox input[type="checkbox"] {
  width: auto;
  margin: 0;
  padding: 0;
}

.tc-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  overflow: visible;
}

.tc-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.tc-section-header h4 {
  margin: 0;
}

.tc-column-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
  background: #f9fafb;
}

.tc-column-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.tc-column-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.icon-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  min-height: 36px;
}

.icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.icon-btn.danger {
  color: #b91c1c;
}

.tc-column-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 12px;
}

.tc-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef2ff;
  font-size: 12px;
  color: #374151;
}

.tc-summary-text {
  font-size: 12px;
  color: #374151;
  line-height: 1.4;
}

.tc-summary-text.muted {
  color: #6b7280;
}

.tc-preview-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
  font-size: 12px;
  color: #6b7280;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.btn-option {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.btn-primary {
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
}

/* =========================
   NESTED CONSTRAINTS MODAL
   ========================= */
.nested-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.nested-modal {
  width: min(92vw, 980px);
  max-width: 980px;
  max-height: 90vh;
  overflow-y: auto;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 20px 22px;
  border-radius: 14px;
}

.nested-modal :deep(.modal),
.nested-modal :deep(.constraints-edit-modal),
.nested-modal :deep(.constraints-dialog),
.nested-modal :deep(.field-constraints-dialog) {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.nested-modal :deep(.row),
.nested-modal :deep(.row.two) {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.nested-modal :deep(input),
.nested-modal :deep(select),
.nested-modal :deep(textarea) {
  box-sizing: border-box;
  max-width: 100%;
}

/* =========================
   TABLE SHELL
   ========================= */
.table-runtime-shell {
  width: 100%;
}

.table-runtime-wrap {
  overflow-x: auto;
  overflow-y: visible;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  position: relative;
  width: 100%;
}

.preview-wrap {
  margin-top: 10px;
}

.table-runtime {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

.table-runtime th,
.table-runtime td {
  border: 1px solid #e5e7eb;
  padding: 10px 14px;
  text-align: left;
  vertical-align: top;
  box-sizing: border-box;
  white-space: normal;
}

.rowno-col {
  width: 56px;
  min-width: 56px;
  max-width: 56px;
  text-align: center;
}

.row-actions-col,
.row-actions-cell {
  width: 128px;
  min-width: 128px;
  max-width: 128px;
  vertical-align: top;
}

.table-cell-box {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  min-width: 220px;
  width: 100%;
  overflow: visible;
}

.table-cell-control {
  width: 100%;
  min-width: 220px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  overflow: visible;
  position: relative;
}

.table-cell-control > * {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.cell-help {
  margin-top: 6px;
  font-style: italic;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.35;
  text-align: left;
}

.table-cell-error {
  margin-top: 6px;
  color: #dc2626;
  font-size: 12px;
  line-height: 1.35;
  text-align: left;
  word-break: break-word;
}

/* =========================
   STANDARD INPUTS
   ========================= */
.table-cell-control :deep(input[type="text"]),
.table-cell-control :deep(input[type="number"]),
.table-cell-control :deep(textarea),
.table-cell-control :deep(select) {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #111827;
}

.table-cell-control :deep(input[type="text"]),
.table-cell-control :deep(input[type="number"]),
.table-cell-control :deep(select) {
  min-height: 44px;
  padding: 10px 12px;
}

.table-cell-control :deep(textarea) {
  min-height: 88px;
  padding: 10px 12px;
  resize: vertical;
}

.table-cell-control :deep(input::placeholder),
.table-cell-control :deep(textarea::placeholder) {
  color: #9ca3af;
  opacity: 1;
}

/* =========================
   CUSTOM COMPONENT WRAPPERS
   ========================= */
.table-cell-control :deep(.field-checkbox),
.table-cell-control :deep(.field-radio-group),
.table-cell-control :deep(.radio-group),
.table-cell-control :deep(.radio-options),
.table-cell-control :deep(.field-select),
.table-cell-control :deep(.select-wrapper),
.table-cell-control :deep(.date-format-picker),
.table-cell-control :deep(.field-time),
.table-cell-control :deep(.time-field),
.table-cell-control :deep(.date-picker-wrapper),
.table-cell-control :deep(.ftm-wrap),
.table-cell-control :deep(.dfp-wrapper) {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: visible !important;
  box-sizing: border-box;
}

/* keep regular field controls aligned at top */
.table-cell-control :deep(.field-select),
.table-cell-control :deep(.select-wrapper),
.table-cell-control :deep(.dfp-wrapper),
.table-cell-control :deep(.ftm-wrap),
.table-cell-control :deep(.field-checkbox),
.table-cell-control :deep(.field-radio-group),
.table-cell-control :deep(.radio-group),
.table-cell-control :deep(.radio-options) {
  align-self: flex-start;
}

/* Date/time/select input sizing */
.table-cell-control :deep(.date-format-picker input),
.table-cell-control :deep(.field-time input),
.table-cell-control :deep(.time-field input),
.table-cell-control :deep(.field-select select),
.table-cell-control :deep(.select-wrapper select),
.table-cell-control :deep(input[type="date"]),
.table-cell-control :deep(input[type="time"]),
.table-cell-control :deep(.ftm-input),
.table-cell-control :deep(.dp__input),
.table-cell-control :deep(.dfp-input) {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
  min-height: 44px;
  box-sizing: border-box !important;
}

/* Select shouldn't overflow its cell */
.table-cell-control :deep(.field-select),
.table-cell-control :deep(.select-wrapper) {
  position: relative;
  z-index: 1;
}

/* Radio/checkbox shouldn't distort column width */
.table-cell-control :deep(.field-radio-group),
.table-cell-control :deep(.radio-group),
.table-cell-control :deep(.radio-options) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.table-cell-control :deep(.field-checkbox) {
  display: inline-flex;
  align-items: center;
}

/* =========================
   POPUP LAYERS
   ========================= */

/* Time popover can remain inside table if overflow is visible */
.table-cell-control :deep(.ftm-pop) {
  z-index: 9999 !important;
}

/* For date picker, the menu should not be clipped */
.table-runtime-wrap :deep(.dp__menu),
.table-runtime-wrap :deep(.dp__menu_wrapper),
.table-runtime-wrap :deep(.dp__outer_menu_wrap) {
  z-index: 9999 !important;
}

/* If vue datepicker is still visually clipped by ancestor stacking,
   this keeps its wrapper above neighboring cells */
.table-cell-control :deep(.dfp-wrapper),
.table-cell-control :deep(.date-picker-wrapper),
.table-cell-control :deep(.date-format-picker) {
  position: relative;
  z-index: 30;
}

.table-cell-control :deep(.ftm-wrap),
.table-cell-control :deep(.field-time),
.table-cell-control :deep(.time-field) {
  position: relative;
  z-index: 30;
}

/* =========================
   ACTIONS
   ========================= */
.table-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-start;
}

.btn-row-delete {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 13px;
}

.btn-row-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* =========================
   RESPONSIVE
   ========================= */
@media (max-width: 900px) {
  .tc-grid,
  .tc-column-grid {
    grid-template-columns: 1fr;
  }

  .tc-column-top {
    align-items: flex-start;
    flex-direction: column;
  }

  .nested-modal {
    width: min(96vw, 980px);
    padding: 16px;
  }

  .table-runtime {
    min-width: 980px;
  }
}
.required-star {
  color: #dc2626;
  font-weight: 700;
  line-height: 1;
}
</style>
